import { type ReactNode, useState } from "react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Icon } from "../ui/icon";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

// The parts that the sign-in page and the password recovery page share. They draw; they decide nothing about who may sign in (the auth
// provider and the API do).

/** The page around a sign-in card: centred, the brand mark above the card. */
export function AuthShell({
  titleId,
  title,
  children,
}: Readonly<{ titleId: string; title: string; children: ReactNode }>) {
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-4 py-10">
      <div className="grid w-full max-w-md gap-7">
        <div className="flex items-center justify-center gap-3">
          <span
            aria-hidden="true"
            className="grid size-11 place-items-center rounded-md bg-primary font-serif text-[28px] leading-8 font-medium text-primary-foreground"
          >
            L
          </span>
          <p className="m-0 font-serif text-[34px] leading-10 font-medium tracking-[-0.01em]">
            Lafayette
          </p>
        </div>
        <section aria-labelledby={titleId}>
          <Card className="grid gap-5 p-6 sm:p-8">
            <h1
              id={titleId}
              className="m-0 font-serif text-[34px] leading-10 font-medium tracking-[-0.015em]"
            >
              {title}
            </h1>
            {children}
          </Card>
        </section>
      </div>
    </main>
  );
}

/** A labelled text control of the sign-in forms (the kit `TextField` has no `email` or `password` type). */
export function AuthField({
  id,
  label,
  hint,
  children,
}: Readonly<{ id: string; label: string; hint?: string; children: ReactNode }>) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {hint ? (
        <p id={`${id}-hint`} className="m-0 text-sm leading-5 text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {children}
    </div>
  );
}

/**
 * A password control with a "show" button, so a person can check what they typed on a phone. The control keeps its `autoComplete` value (so a
 * password manager knows what to fill or to offer) and is `type="password"` again whenever the form is not showing it.
 */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  name,
  minLength,
  showLabel,
  describedBy,
}: Readonly<{
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
  name: string;
  minLength?: number;
  /** The name of the button, for example "Show password". It is fixed: the state is `aria-pressed`, so a screen reader does not hear two names. */
  showLabel: string;
  describedBy?: string;
}>) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        name={name}
        type={shown ? "text" : "password"}
        autoComplete={autoComplete}
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        minLength={minLength}
        aria-describedby={describedBy}
        required
        className="rounded-md pr-12 text-base md:text-base"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={showLabel}
        aria-pressed={shown}
        onClick={() => setShown((current) => !current)}
        className="absolute inset-y-0 right-0 text-muted-foreground"
      >
        {shown ? <Icon name="view-off" /> : <Icon name="view" />}
      </Button>
    </div>
  );
}
